import React, { useEffect, useRef } from 'react';
import Text from '@service-victoria/component-library/components/Text';
import colors from '@service-victoria/component-library/utils/colors';
import { StepComponent } from '@service-victoria/component-library/components/Wizard/props';
import * as Interfaces from 'interfaces';
import { storage, constants, logger } from 'utils';
import * as Styled from '../styled';
import { useHistory } from 'react-router-dom';
import InlinePermit from './InlinePermit';
import { ExternalLinks } from 'utils/constants';
import { CompletionSurvey } from 'Components';
import { trackEvent } from 'utils/analytics';
import { getFeedbackApiDomain } from 'utils/url';
import { sendFeedback } from 'services';
import { isExemptionApplication } from 'steps/Assess/travel-reasons';
import { isReturnExemption } from 'steps/Assess/travel-reasons';
import { isEmpty } from 'lodash-es';

export type ComponentProps = StepComponent<Interfaces.Common.FormValues>;

const Done: React.FC<ComponentProps> = ({ context }) => {
  const timeout = useRef<NodeJS.Timeout>();
  const appContext = context.appStates[constants.StorageKeys.State] as Interfaces.Store.AppContext;
  const { transactionID, travelReasons } = appContext.currentState;
  const { setValues } = context.form;
  const { applicationId } = context.form.values;

  const history = useHistory();

  useEffect(() => {
    if (!applicationId) {
      timeout.current = setTimeout(() => {
        history.push(constants.Routes.Home);
      }, 200);
    } else if (timeout.current) {
      clearTimeout(timeout.current);
    }
  }, [applicationId, history]);

  useEffect(() => {
    history.listen(() => {
      if (history.action === 'POP') {
        // @ts-expect-error - If we don't set the values as empty, the back processes first and re-syncs all of the values in storage
        setValues({}, false);
        storage.clear();
        window.location.href = process.env.PUBLIC_URL || '/';
      }
    });
  }, [history, setValues]);

  useEffect(() => {
    storage.clear();
    logger.info(`Completed transaction for ${transactionID}`, {
      transactionID,
      permitId: applicationId
    });
  }, [transactionID, applicationId]);

  if (isEmpty(context.form.values)) {
    return null;
  }

  const isExemption =
    isExemptionApplication(context.form.values.assess.reasons) ||
    isReturnExemption(context.form.values.assess.reasons);

  const submittedReason =
    travelReasons.status === 'success'
      ? travelReasons.reasons.find(reason => reason.value === context.form.values.assess.reasons)
      : null;

  return (
    <Styled.Wrapper>
      <Styled.DoneWrapper
        id='done-wrapper'
        data-testid='Done-Success-Message'
        data-application-id={applicationId}
        data-permit-reason-category={isExemption ? 'exemption' : submittedReason?.zone}
        data-permit-reason-label={submittedReason?.label}
        data-permit-reason-id={submittedReason?.value}>
        {isExemption ? (
          <>
            <Styled.ClockWrapper fill={colors.orange} />
            <Text.Heading.H1 fontWeight='bold'>Being Processed</Text.Heading.H1>
          </>
        ) : (
          <>
            <Styled.TickWrapper />
            <Text.Heading.H1 fontWeight='bold'>Success</Text.Heading.H1>
          </>
        )}
      </Styled.DoneWrapper>
      <Styled.Spacer8 />
      <InlinePermit {...context.form.values} />
      <div data-testid='Done-Confirmation'>
        <Text.Body as='div'>
          We&apos;ve sent you an email about your {isExemption && 'exemption'} application to enter
          Victoria.
          <br />
          <Text.Link
            target='_blank'
            href={`${ExternalLinks.CoronavirusVicQandA}#getting-help-with-your-permit-application`}
            value="Email didn't arrive?"
          />
        </Text.Body>
        <Styled.Spacer24 />
        {isExemption ? (
          <>
            <Text.Heading.H3 fontWeight={600}>
              Your exemption has not been granted yet.
              <br />
              <br />
              Do not travel until:
            </Text.Heading.H3>
            <Styled.Spacer24 />
            <Styled.List>
              <Styled.ListItem>
                The Department of Health has sent you a formal exemption letter, confirming your
                exemption has been granted.
              </Styled.ListItem>
            </Styled.List>
          </>
        ) : (
          <Text.Body>
            Please remember to keep your permit or exemption letter with you when you travel.
          </Text.Body>
        )}
        <Styled.Spacer48 />
        <Text.Heading.H3 fontWeight={600}>Staying safe</Text.Heading.H3>
        <Styled.Spacer24 />
        <Styled.List>
          <Styled.ListItem>Install the COVIDSafe app on your phone</Styled.ListItem>
          <Styled.ListItem>
            <Text.Link
              target='_blank'
              value='Get tested'
              href='https://www.dhhs.vic.gov.au/getting-tested-coronavirus-covid-19#where-to-get-tested'
            />{' '}
            if you feel unwell and stay home
          </Styled.ListItem>
        </Styled.List>
      </div>
      <CompletionSurvey
        onThumbSelect={type => {
          trackEvent({
            category: 'FormCompletionFeedback',
            action: 'covid-travel',
            label: `thumbs-${type}`
          });
        }}
        allowTextFeedback
        onFeedbackGiven={async (type, message) => {
          const isThumbsUp = type === 'up';
          const baseUrl = getFeedbackApiDomain();
          try {
            await sendFeedback(baseUrl, isThumbsUp, message, transactionID);
          } catch (error) {
            logger.error('Feedback error', error);
          }
        }}
      />
    </Styled.Wrapper>
  );
};

export default Done;
